import {
  Package,
  Home,
  ChevronRight,
  RefreshCw,
  Plus,
  Filter,
} from "lucide-react";
import { useLocation } from "react-router-dom";

const Header = ({
  title,
  subtitle,
  onRefresh,
  refreshing = false,
  onAddNew,
  addNewLabel = "Add New",
  showFilter = false,
  onFilterToggle,
  filterActive = false,
  actions = [],
}) => {
  const location = useLocation();

  // Generate breadcrumb from current path
  const generateBreadcrumb = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ label: "Home", path: "/" }];

    pathSegments.forEach((segment, index) => {
      const path = "/" + pathSegments.slice(0, index + 1).join("/");
      const label = segment.charAt(0).toUpperCase() + segment.slice(1);
      breadcrumbs.push({ label, path });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumb();

  return (
    <header className="page-header">
      <div className="header-content">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav" aria-label="Breadcrumb">
          <ol className="breadcrumb-list">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.path} className="breadcrumb-item">
                {index === 0 ? (
                  <Home size={14} className="breadcrumb-icon" />
                ) : (
                  <ChevronRight size={12} className="breadcrumb-separator" />
                )}
                {index === breadcrumbs.length - 1 ? (
                  <span className="breadcrumb-current">{crumb.label}</span>
                ) : (
                  <a href={crumb.path} className="breadcrumb-link">
                    {crumb.label}
                  </a>
                )}
              </li>
            ))}
          </ol>
        </nav>

        {/* Page Title Section */}
        <div className="page-title-section">
          <div className="title-content">
            <h1 className="page-title">
              <Package className="title-icon" size={24} />
              {title}
            </h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>

          {/* Action Buttons */}
          <div className="header-actions">
            {onRefresh && (
              <button
                className="btn-action btn-secondary"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh data"
              >
                <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
                <span className="d-none d-sm-inline">Refresh</span>
              </button>
            )}

            {showFilter && (
              <button
                className={`btn-action ${
                  filterActive ? "btn-primary" : "btn-secondary"
                }`}
                onClick={onFilterToggle}
                title="Toggle filters"
              >
                <Filter size={16} />
                <span className="d-none d-sm-inline">
                  {filterActive ? "Hide Filters" : "Show Filters"}
                </span>
              </button>
            )}

            {/* Custom actions */}
            {actions.map((action, index) => (
              <button
                key={index}
                className={`btn-action ${action.variant || "btn-secondary"}`}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title}
              >
                {action.icon && action.icon}
                <span
                  className={action.mobileHidden ? "d-none d-sm-inline" : ""}
                >
                  {action.label}
                </span>
              </button>
            ))}

            {onAddNew && (
              <button
                className="btn-action btn-primary"
                onClick={onAddNew}
                title={`Add new ${title.toLowerCase()}`}
              >
                <Plus size={16} />
                <span className="d-none d-sm-inline">{addNewLabel}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .page-header {
          background: white;
          border-bottom: 1px solid var(--gray-200);
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: var(--shadow-sm);
        }

        .header-content {
          padding: 1.5rem 1.5rem 1rem;
          max-width: 100%;
        }

        /* Breadcrumb Styles */
        .breadcrumb-nav {
          margin-bottom: 0.75rem;
        }

        .breadcrumb-list {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          padding: 0;
          list-style: none;
          flex-wrap: wrap;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .breadcrumb-icon {
          color: var(--gray-500);
        }

        .breadcrumb-separator {
          color: var(--gray-400);
        }

        .breadcrumb-link {
          color: var(--gray-600);
          text-decoration: none;
          transition: color var(--transition-fast);
        }

        .breadcrumb-link:hover {
          color: var(--primary-600);
          text-decoration: none;
        }

        .breadcrumb-current {
          color: var(--gray-900);
          font-weight: 500;
        }

        /* Page Title Section */
        .page-title-section {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .title-content {
          flex: 1;
          min-width: 200px;
        }

        .page-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0 0 0.25rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .title-icon {
          color: var(--primary-600);
        }

        .page-subtitle {
          margin: 0;
          color: var(--gray-600);
          font-size: 0.875rem;
          line-height: 1.4;
        }

        /* Header Actions */
        .header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        @media (max-width: 768px) {
          .header-content {
            padding: 1rem;
          }

          .page-title-section {
            flex-direction: column;
            align-items: stretch;
          }

          .header-actions {
            justify-content: flex-end;
            margin-top: 1rem;
          }

          .page-title {
            font-size: 1.25rem;
          }
        }

        @media (max-width: 575px) {
          .breadcrumb-list {
            gap: 0.25rem;
          }

          .breadcrumb-item {
            font-size: 0.8125rem;
          }

          .header-actions {
            gap: 0.5rem;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
